/**
 * KAN-883 (решение владельца): период активности доски CRM вместо active_days.
 * На доске карточки с последней активностью в выбранном периоде (дни по Москве,
 * включительно); "Всё время" - без фильтра (по умолчанию).
 * Адрес: ?activity_from=&activity_to= - свои параметры доски, чтобы не
 * пересекаться с периодом таблицы (?from=&to=, по умолчанию сегодня) на той же
 * странице. Старые ссылки ?active=7|30 (и ?active_days=N) читаются как последние
 * N дней; при выборе нового периода старый параметр убирается.
 * В запрос доски уходят date_from / date_to.
 * KAN-910: быстрый период именем ?activity=today|yesterday|7d|all (lib/urlPeriod), главнее дат;
 * даты activity_from/activity_to только из календаря; без периода пишется ?activity=all.
 */
import { addDays, clampCrmRange } from './crmRange';
import { todayMoscow, type CrmRange } from './crmTable';
import type { PeriodChoice } from './periodPresets';
import { parsePeriodName, parseUrlPeriod, PERIOD_URL_ALL, withUrlPeriod, type UrlPeriodKeys, type UrlPeriodValue } from './urlPeriod';

export const CRM_BOARD_ACTIVITY_PARAM = { period: 'activity', from: 'activity_from', to: 'activity_to' } as const;
export const CRM_BOARD_ACTIVITY_LEGACY_PARAMS = ['active', 'active_days'] as const;
const LEGACY_PARAMS = CRM_BOARD_ACTIVITY_LEGACY_PARAMS;
const KEYS: UrlPeriodKeys = CRM_BOARD_ACTIVITY_PARAM;

/** Период доски по умолчанию (имя в адресе): всё время. */
export const CRM_BOARD_DEFAULT_ACTIVITY = PERIOD_URL_ALL;

/** Быстрые кнопки периода доски. */
export const CRM_BOARD_PERIOD_CHOICES: readonly PeriodChoice[] = ['today', 'yesterday', 'week', 'all'];

/** Период активности из адреса; null - всё время. */
export function parseBoardActivity(params: URLSearchParams, now: Date = new Date()): CrmRange | null {
  const from = params.get(CRM_BOARD_ACTIVITY_PARAM.from);
  const to = params.get(CRM_BOARD_ACTIVITY_PARAM.to);
  if (parsePeriodName(params.get(KEYS.period)) || from || to) return parseUrlPeriod(params, KEYS, 'all', { now }).range;
  for (const name of LEGACY_PARAMS) {
    const days = Number(params.get(name));
    if (Number.isInteger(days) && days > 0) {
      const today = todayMoscow(now);
      return clampCrmRange({ from: addDays(today, -(days - 1)), to: today });
    }
  }
  return null;
}

/**
 * Адрес с периодом активности: даты - activity_from/activity_to, быстрый пункт - ?activity=<имя>,
 * null ("Всё время") - ?activity=all. Старые active/active_days снимаются.
 */
export function withBoardActivity(prev: URLSearchParams, value: UrlPeriodValue | null): URLSearchParams {
  const next = new URLSearchParams(prev);
  LEGACY_PARAMS.forEach((name) => next.delete(name));
  return withUrlPeriod(next, KEYS, value ?? 'all');
}

/** Параметры запроса доски: date_from / date_to только при периоде. */
export function boardActivityParams(range: CrmRange | null): { date_from?: string; date_to?: string } {
  return range ? { date_from: range.from, date_to: range.to } : {};
}
